<script setup lang="ts">
import { computed } from 'vue'
import type { CreditSearchPreviewCourse } from '@/models/credit-search/preview'

defineOptions({ name: 'CreditSearchPathwayCreditTable' })

const props = defineProps<{
  course: CreditSearchPreviewCourse
  refreshing: boolean
  selectionKey: string
}>()

const emit = defineEmits<{
  remove: [selectionKey: string]
}>()

type PathwayCreditRow = {
  key: string
  showDelete: boolean
  deleteRowSpan: number
  showReference: boolean
  reference: string
  referenceRowSpan: number
  groupLabel: string
  subjectCode: string | null
  subjectName: string | null
  noCredit: boolean
  conditionNumber: number | null
}

const conditions = computed(() => {
  const result: string[] = []
  for (const agreement of props.course.articulation_agreements) {
    for (const section of agreement.articulation_sections) {
      if (section.conditions) result.push(section.conditions)
    }
  }
  return result
})

const rows = computed<PathwayCreditRow[]>(() => {
  const result: PathwayCreditRow[] = []
  let conditionNumber = 0

  for (const [agreementIndex, agreement] of props.course.articulation_agreements.entries()) {
    const agreementRowCount = agreement.articulation_sections.reduce(
      (total, section) => total + (section.subject_groups.length === 0
        ? 1
        : section.subject_groups.reduce((sectionTotal, group) => sectionTotal + group.subjects.length, 0)),
      0,
    )
    let agreementFirstRow = true

    for (const [sectionIndex, section] of agreement.articulation_sections.entries()) {
      const sectionConditionNumber = section.conditions ? ++conditionNumber : null
      if (section.subject_groups.length === 0) {
        result.push({
          key: `${agreementIndex}-${sectionIndex}-no-credit`,
          showDelete: result.length === 0,
          deleteRowSpan: 0,
          showReference: agreementFirstRow,
          reference: agreement.reference,
          referenceRowSpan: agreementRowCount,
          groupLabel: '',
          subjectCode: null,
          subjectName: null,
          noCredit: true,
          conditionNumber: sectionConditionNumber,
        })
        agreementFirstRow = false
        continue
      }

      for (const [groupIndex, group] of section.subject_groups.entries()) {
        for (const [subjectIndex, subject] of group.subjects.entries()) {
          result.push({
            key: `${agreementIndex}-${sectionIndex}-${groupIndex}-${subjectIndex}`,
            showDelete: result.length === 0,
            deleteRowSpan: 0,
            showReference: agreementFirstRow,
            reference: agreement.reference,
            referenceRowSpan: agreementRowCount,
            groupLabel: agreementFirstRow && group.subjects.length > 1 ? 'One of' : '',
            subjectCode: subject.subject_code,
            subjectName: `${subject.subject_name}${subjectIndex === group.subjects.length - 1 ? '' : ', or'}`,
            noCredit: false,
            conditionNumber: sectionConditionNumber,
          })
          agreementFirstRow = false
        }
      }
    }
  }

  const totalRows = result.length
  return result.map((row) => ({ ...row, deleteRowSpan: totalRows }))
})
</script>

<template>
  <table class="table table-sm PrecedentsTableCourses">
    <thead>
      <tr>
        <th colspan="2">Courses Credit</th>
        <th colspan="3">Matching La Trobe Subjects</th>
      </tr>
      <tr>
        <td></td>
        <th>Reference</th>
        <td></td>
        <th>Code</th>
        <th colspan="2">Name</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in rows" :key="row.key">
        <td v-if="row.showDelete" class="credit-preview-delete-cell" :rowspan="row.deleteRowSpan">
          <button
            type="button"
            class="btn btn-danger btn-sm"
            aria-label="Delete"
            :disabled="props.refreshing"
            @click="emit('remove', props.selectionKey)"
          >X</button>
        </td>
        <td v-if="row.showReference" :rowspan="row.referenceRowSpan">{{ row.reference }}</td>
        <template v-if="row.noCredit">
          <td>{{ row.groupLabel }}</td>
          <td colspan="3">
            No Credit<sup v-if="row.conditionNumber">{{ row.conditionNumber }}</sup>
          </td>
        </template>
        <template v-else>
          <td>{{ row.groupLabel }}</td>
          <td>
            {{ row.subjectCode }}<sup v-if="row.conditionNumber">{{ row.conditionNumber }}</sup>
          </td>
          <td colspan="2">{{ row.subjectName }}</td>
        </template>
      </tr>
    </tbody>
  </table>
  <template v-if="conditions.length > 0">
    <h4>Conditions</h4>
    <ol>
      <li v-for="(condition, index) in conditions" :key="`${condition}-${index}`">{{ condition }}</li>
    </ol>
  </template>
</template>

<style lang="scss" scoped>
td,
th {
  vertical-align: top;
}

</style>
